/**
 * Generic Wizard Layout
 * Full-screen layout with header, sidebar, and content area
 */

import React, { Suspense } from 'react';
import { useWizardContext } from './WizardContext';
import { WizardLayoutProps } from './types';
import WizardHeader from './WizardHeader';
import WizardSidebar from './WizardSidebar';
import WizardFooter from './WizardFooter';

const WizardLayout: React.FC<WizardLayoutProps> = ({ clientName, onClose }) => {
  const { config, currentStep } = useWizardContext();

  /**
   * Render current step component
   */
  const renderCurrentStep = () => {
    const step = config.steps.find(s => s.id === currentStep);

    if (!step) {
      return (
        <div className="text-center text-gray-500">
          Step not found
        </div>
      );
    }

    const StepComponent = step.component;

    return (
      <Suspense
        fallback={
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-kielis-gold border-t-transparent"></div>
          </div>
        }
      >
        <StepComponent />
      </Suspense>
    );
  };

  return (
    <div className={`flex flex-col h-screen ${config.theme.contentBg}`}>
      {/* Header - z-20 ensures it's above scrollable content */}
      <WizardHeader clientName={clientName} onClose={onClose} />

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar navigation */}
        <WizardSidebar />

        {/* Form content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Scrollable form area */}
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto">
              {renderCurrentStep()}
            </div>
          </div>

          {/* Fixed navigation footer */}
          <WizardFooter />
        </main>
      </div>
    </div>
  );
};

export default WizardLayout;
